// Analytics consent gate, ported from the legacy app's geo-aware model
// (apps/old-web/lib/consent.ts) for the Vite SPA.
//
// Jurisdiction model:
//  - EU 27 + UK + EEA (IS/LI/NO): analytics needs explicit opt-in, so the
//    consent banner shows and nothing fires until the visitor accepts.
//  - Everywhere else (JP included): analytics runs without a banner; the
//    disclosure lives in the /docs privacy policy (APPI is disclosure-based).
//  - UNKNOWN geo: treated as consent-required — the safe side — so the banner
//    shows and nothing fires until accepted.
//
// The visitor country comes from /api/geo (a Pages Function reading
// Cloudflare's edge geolocation — no external lookup; a specific-file route,
// so it never touches the /v1 BFF forwarder). Fetched once and cached in
// sessionStorage. The lookup FAILS OPEN to "unknown": a geo outage must never
// block rendering or any app feature — it only means the banner shows.
// Consent geo is privacy posture, never a commerce or country-capability
// authority (those stay with /api).

// Same storage keys as the legacy web app so a visitor's prior choice on
// tinkrapp.com properties keeps its meaning.
const CONSENT_KEY = "tinkr-cookie-consent";
const PREFS_KEY = "tinkr-cookie-prefs";
const GEO_CACHE_KEY = "tinkr-geo-country";
// sessionStorage marker for an AUTHORITATIVE "the edge does not know" answer
// (Cloudflare XX/T1 or a null hint). Distinct from a fetch failure, which is
// never cached so a transient outage can recover within the session.
const GEO_UNKNOWN = "unknown";

/**
 * EU member states + EEA (Iceland, Liechtenstein, Norway) + UK: the
 * jurisdictions that require opt-in consent for analytics cookies under
 * GDPR / UK GDPR / ePrivacy.
 */
export const CONSENT_REQUIRED_COUNTRIES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
  "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL",
  "PL", "PT", "RO", "SK", "SI", "ES", "SE", // EU 27
  "GB", // UK
  "IS", "LI", "NO", // EEA (non-EU)
]);

/** Unknown country (null) is consent-required on purpose: safe side. */
export function requiresConsent(countryCode: string | null): boolean {
  if (!countryCode) return true;
  return CONSENT_REQUIRED_COUNTRIES.has(countryCode.toUpperCase());
}

export type ConsentChoice = "all" | "essential" | null;
// Retain an explicit choice for this document even if browser storage is blocked.
let pageChoice: ConsentChoice = null;

export interface CookiePreferences {
  analytics: boolean;
  marketing: boolean;
}

export function getConsent(): ConsentChoice {
  if (pageChoice !== null) return pageChoice;
  try {
    const stored = window.localStorage.getItem(CONSENT_KEY);
    if (stored === "all" || stored === "essential") return stored;
    // "custom" is a legacy-app value; its prefs payload decides, so surface it
    // as a made choice and let getPreferences read the detail.
    if (stored === "custom") return "essential";
  } catch {
    // Storage unavailable (private mode): no durable choice exists.
  }
  return null;
}

export function getPreferences(): CookiePreferences {
  // Explicit accept/decline is authoritative even if writing the separate
  // legacy preference JSON failed and left an older value behind.
  let choice: string | null = pageChoice;
  try { choice ??= window.localStorage.getItem(CONSENT_KEY); } catch { /* No durable storage. */ }
  if (choice === "all" || choice === "essential") {
    return { analytics: choice === "all", marketing: false };
  }
  try {
    const stored = window.localStorage.getItem(PREFS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<CookiePreferences> | null;
      return {
        analytics: parsed?.analytics === true,
        marketing: parsed?.marketing === true,
      };
    }
  } catch {
    // Invalid JSON or storage unavailable: fall through to the choice value.
  }
  return getConsent() === "all"
    ? { analytics: true, marketing: false }
    : { analytics: false, marketing: false };
}

export function setConsent(choice: "all" | "essential"): void {
  pageChoice = choice;
  const prefs: CookiePreferences =
    choice === "all"
      ? { analytics: true, marketing: false }
      : { analytics: false, marketing: false };
  try {
    window.localStorage.setItem(CONSENT_KEY, choice);
    // Prefer durable reads when available so a choice in another tab is seen.
    pageChoice = null;
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // A failed primary write retains the document's choice. A failed legacy
    // prefs write is harmless: the primary all/essential value is authoritative.
  }
}

async function fetchGeoCountry(): Promise<string | null> {
  const response = await fetch("/api/geo", { headers: { accept: "application/json" } });
  if (!response.ok) throw new Error(`geo lookup failed: ${response.status}`);
  const body = (await response.json()) as { country?: unknown };
  const raw = typeof body.country === "string" ? body.country.trim().toUpperCase() : "";
  return /^[A-Z]{2}$/.test(raw) ? raw : null;
}

let geoPromise: Promise<string | null> | null = null;

/**
 * The visitor's country hint for consent purposes. Resolved at most once per
 * page load (deduped promise), persisted per browser session (sessionStorage).
 * NEVER throws — a failed lookup resolves null ("unknown"), which the consent
 * model treats as GDPR-side.
 */
export function getGeoCountry(): Promise<string | null> {
  if (geoPromise) return geoPromise;
  try {
    const cached = window.sessionStorage.getItem(GEO_CACHE_KEY);
    if (cached === GEO_UNKNOWN) return Promise.resolve(null);
    if (cached && /^[A-Z]{2}$/.test(cached)) return Promise.resolve(cached);
  } catch {
    // sessionStorage unavailable: fall through to the network hint.
  }
  geoPromise = fetchGeoCountry()
    .then((code) => {
      try {
        window.sessionStorage.setItem(GEO_CACHE_KEY, code ?? GEO_UNKNOWN);
      } catch {
        // Cache miss only; the in-flight promise still dedupes this load.
      }
      return code;
    })
    .catch(() => {
      // Fail OPEN to unknown, and do NOT cache the failure: a later page in
      // the same session may succeed. Clear the memoized rejection so a retry
      // is possible on the next call.
      geoPromise = null;
      return null;
    });
  return geoPromise;
}

/**
 * Whether analytics may run right now.
 *  - An explicit stored choice always wins (either way, any geography).
 *  - No stored choice: allowed only OUTSIDE consent-required jurisdictions;
 *    unknown geography counts as consent-required.
 */
export async function hasAnalyticsConsent(): Promise<boolean> {
  const choice = getConsent();
  if (choice !== null) return getPreferences().analytics;
  const country = await getGeoCountry();
  // A visitor may have decided while the location lookup was pending.
  return getConsent() !== null ? getPreferences().analytics : !requiresConsent(country);
}

/** Whether the consent banner should be shown (no stored choice + GDPR/unknown geo). */
export async function consentBannerNeeded(): Promise<boolean> {
  if (getConsent() !== null) return false;
  const country = await getGeoCountry();
  return getConsent() === null && requiresConsent(country);
}

/** Test-only: drop the memoized geo lookup so each test starts cold. */
export function resetGeoCacheForTest(): void {
  geoPromise = null;
}
